"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";

/**
 * Full-viewport fixed background layer that fades subtle radial tints
 * between sections as the user scrolls. A base `#f5f5f6` floor stays
 * constant; each preset tint is stacked on top and cross-faded via
 * opacity so colors can't pop — they creep in gently.
 *
 * Any page / component can opt into tinted scroll simply by giving a
 * section `data-bg-tint="warm|cool|peach|lilac|mint|neutral"`. The
 * fader re-scans on every route change so new pages pick up automatically.
 */
const TINT_PRESETS = {
  // Warm surgical amber — Renaissance energy.
  warm:
    "radial-gradient(ellipse 85% 65% at 18% 22%, rgba(255, 132, 96, 0.22), transparent 62%), radial-gradient(ellipse 70% 50% at 82% 78%, rgba(255, 90, 110, 0.14), transparent 65%)",
  // Cool focused blue — strategy, depth.
  cool:
    "radial-gradient(ellipse 80% 60% at 80% 18%, rgba(86, 130, 214, 0.20), transparent 62%), radial-gradient(ellipse 75% 55% at 18% 82%, rgba(120, 170, 220, 0.14), transparent 65%)",
  // Warm paper / peach — published works, editorial.
  peach:
    "radial-gradient(ellipse 85% 60% at 25% 18%, rgba(255, 168, 110, 0.22), transparent 62%), radial-gradient(ellipse 70% 50% at 82% 82%, rgba(235, 110, 90, 0.14), transparent 65%)",
  // Cinematic lilac / soft violet — media, highlights.
  lilac:
    "radial-gradient(ellipse 85% 60% at 70% 20%, rgba(170, 130, 220, 0.20), transparent 62%), radial-gradient(ellipse 70% 50% at 18% 80%, rgba(210, 140, 200, 0.14), transparent 65%)",
  // Soft mint — renewal, pursuit.
  mint:
    "radial-gradient(ellipse 80% 60% at 20% 80%, rgba(110, 200, 170, 0.18), transparent 62%), radial-gradient(ellipse 70% 50% at 80% 20%, rgba(140, 210, 200, 0.12), transparent 65%)",
  // Dusty rose — community, warmth.
  rose:
    "radial-gradient(ellipse 80% 60% at 25% 75%, rgba(255, 150, 170, 0.20), transparent 62%), radial-gradient(ellipse 70% 50% at 78% 22%, rgba(255, 180, 180, 0.12), transparent 65%)",
  // Explicit neutral — page stays on the base #f5f5f6.
  neutral: null,
};

// Legacy section IDs from the homepage's original mapping. If a section
// has neither `data-bg-tint` nor a matching ID here, no tint is applied.
const LEGACY_ID_TO_TINT = {
  story: "warm",
  journey: "cool",
  library: "peach",
  highlights: "lilac",
  reach: null,
};

const TINT_KEYS = Object.keys(TINT_PRESETS).filter((k) => TINT_PRESETS[k]);

export default function SectionBackgroundFader() {
  const pathname = usePathname();
  const [activeTint, setActiveTint] = useState(null);
  const visibleItemsRef = useRef(new Set());

  useEffect(() => {
    // Re-scan the DOM on every route change so pages that arrive via
    // client-side navigation get their tints picked up.
    visibleItemsRef.current = new Set();

    // Collect everything with an explicit tint, then map legacy IDs to
    // tints so the homepage's MaximaTherapy / Waabi / sticky cards /
    // highlights keep working without markup changes.
    const explicit = Array.from(document.querySelectorAll("[data-bg-tint]"));
    const legacy = Object.keys(LEGACY_ID_TO_TINT)
      .map((id) => {
        const el = document.getElementById(id);
        if (!el) return null;
        // Skip if it already has a data-bg-tint (explicit wins).
        if (el.dataset.bgTint) return null;
        return { el, tint: LEGACY_ID_TO_TINT[id] };
      })
      .filter(Boolean);

    // Build a unified array of { el, tint } with DOM-order preserved.
    const all = [
      ...explicit.map((el) => ({ el, tint: el.dataset.bgTint })),
      ...legacy,
    ];
    if (all.length === 0) {
      setActiveTint(null);
      return;
    }
    // Sort by DOM order.
    all.sort((a, b) => {
      if (a.el === b.el) return 0;
      return a.el.compareDocumentPosition(b.el) &
        Node.DOCUMENT_POSITION_FOLLOWING
        ? -1
        : 1;
    });

    const tintByEl = new Map(all.map((item) => [item.el, item.tint]));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visibleItemsRef.current.add(entry.target);
          else visibleItemsRef.current.delete(entry.target);
        });
        // Last visible in DOM order wins — later sections take over as
        // the user scrolls past earlier ones.
        let nextTint = null;
        for (const { el } of all) {
          if (visibleItemsRef.current.has(el)) {
            nextTint = tintByEl.get(el) || null;
          }
        }
        setActiveTint(nextTint);
      },
      { rootMargin: "-30% 0px -30% 0px", threshold: 0 }
    );

    all.forEach(({ el }) => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  return (
    <div className="section-bg-fader" aria-hidden>
      {TINT_KEYS.map((key) => (
        <div
          key={key}
          className="section-bg-fader-layer"
          style={{
            backgroundImage: TINT_PRESETS[key],
            opacity: activeTint === key ? 1 : 0,
          }}
        />
      ))}
    </div>
  );
}
